import type { ImgHTMLAttributes, Ref, SVGAttributes } from 'react';
import { mergeProps } from 'react-aria/mergeProps';
import { useLabels } from '@react-aria/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva, cx } from 'class-variance-authority';

import { useImageLoadingStatus } from './utils';

import styles from './styles/Avatar.module.css';

const avatarStyles = cva(styles.base, {
	variants: {
		size: {
			small: styles.small,
			medium: styles.medium,
			large: styles.large,
		},
	},
	defaultVariants: {
		size: 'medium',
	},
});

const colors = cva(null, {
	variants: {
		color: {
			0: styles.lime,
			1: styles.blue,
			2: styles.pink,
			3: styles.cyan,
			4: styles.purple,
		},
	},
});

interface AvatarVariants extends VariantProps<typeof avatarStyles> {}

interface AvatarProps extends ImgHTMLAttributes<HTMLImageElement>, AvatarVariants {
	ref?: Ref<HTMLImageElement>;
}

interface InitialsAvatarProps extends SVGAttributes<SVGElement>, AvatarVariants {}

const Avatar = ({ className, children, size = 'medium', ref, src, ...props }: AvatarProps) => {
	const status = useImageLoadingStatus(src);
	const labelProps = useLabels(props);

	if (status !== 'loaded') {
		return (
			<InitialsAvatar className={className} size={size} {...labelProps}>
				{children}
			</InitialsAvatar>
		);
	}

	const { alt, ...rest } = props;

	return (
		<img
			ref={ref}
			src={src}
			alt={alt}
			{...mergeProps(rest, labelProps)}
			className={avatarStyles({ size, className })}
		/>
	);
};

const InitialsAvatar = ({ className, size = 'medium', children, ...props }: InitialsAvatarProps) => {
	const color = children ? (children.toString().charCodeAt(0) + children.toString().charCodeAt(1)) % 5 : 0;
	return (
		// biome-ignore lint/a11y/noSvgWithoutTitle: ignore
		<svg
			role="img"
			className={cx(
				avatarStyles({ size, className }),
				colors({ color: color as keyof typeof colors }),
				styles.initials,
			)}
			viewBox="0 0 24 24"
			{...props}
		>
			<text x="50%" y="50%" className={styles.text}>
				{children}
			</text>
		</svg>
	);
};

export { Avatar, InitialsAvatar, avatarStyles };
export type { AvatarProps, InitialsAvatarProps };
